import React, { useState, useRef, useEffect } from "react";
import { Smile, Send } from "lucide-react";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import { marked } from "marked";
import DOMPurify from "dompurify";

const purifyConfig = {
  ADD_TAGS: ["style"],
  ADD_ATTR: ["style"],
  USE_PROFILES: { html: true }
};
interface ChatMessage {
  text: string;
  sender: string;
  parsedObj?: any;
}

interface ChatInterfaceProps {
  agentName: string;
  icon?: string;
  onEndChat: () => void;
  messages: ChatMessage[];
  onSendMessage: (message: string) => void;
  input: string;
  setInput: (input: string) => void;
  typing: boolean;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  agentName,
  icon,
  onEndChat,
  messages,
  onSendMessage,
  input,
  setInput,
  typing
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, typing]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (input.trim()) {
        onSendMessage(input);
      }
    }
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    //@ts-ignore
    setInput((prevInput) => prevInput + emojiData.emoji);
    setShowEmojiPicker(false);
  };
  //@ts-ignore
  const renderContent = (rawContent) => {
    const cleaned = rawContent.replace(/^```html\s*([\s\S]*?)\s*```$/i, "$1");
    if (/<[a-z][\s\S]*>/i.test(cleaned)) {
      return DOMPurify.sanitize(cleaned, purifyConfig);
    }
    return DOMPurify.sanitize(
      //@ts-ignore
      marked.parse(cleaned, { breaks: true }),
      purifyConfig
    );
  };

  return (
    <>
      {/* Google Fonts */}
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Manrope:wght@300;400;500;600&display=swap"
        rel="stylesheet"
      />

      <div className="bg-[#f0f2f5]/50 p-3 sm:p-4 rounded-[30px] sm:rounded-[48px] h-full">
        <div
          className="w-full h-full text-[#111b21] flex flex-col relative overflow-hidden rounded-[2rem] sm:rounded-[3rem] shadow-2xl"
          style={{
            background: "#efeae2",
            fontFamily: "Manrope, sans-serif"
          }}
        >
          {/* Background pattern (optional WhatsApp pattern effect can be added here) */}
          <div className="absolute inset-0 opacity-[0.4] bg-[url('https://i.pinimg.com/originals/8f/ba/cb/8fbacbd464e996966eb9d4a6b7a9c21e.jpg')] bg-repeat bg-[length:400px]"></div>
          {/* Header - Agent photo with title (Left aligned) */}
          <div className="flex-shrink-0 px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-4 z-10 bg-[#f0f2f5] border-b border-[#d1d7db]">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gray-200 border border-gray-300 flex items-center justify-center overflow-hidden">
              {icon ? (
                <img
                  src={icon}
                  alt="Agent"
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                <svg
                  className="w-6 h-6 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
              )}
            </div>
            <h1
              className="text-lg sm:text-xl font-medium text-[#111b21]"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              {agentName}
            </h1>
          </div>

          {/* Main content - Chat Messages with improved layout */}
          <div className="flex-1 flex flex-col min-h-0 relative z-10">
            {/* Chat Messages Area - Reduced spacing */}
            <div className="flex-1 overflow-y-auto px-3 sm:px-4 py-4 touch-scroll">
              <div className="space-y-4">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center py-16">
                    {/* <div className="bg-[#ffeecd] text-[#543b16] px-4 py-2 rounded-lg text-[13px] max-w-xs shadow-sm shadow-[#ffeecd]">
                      Messages are end-to-end encrypted. No one outside of this chat, not even WhatsApp, can read or listen to them. Click to learn more.
                    </div> */}
                  </div>
                ) : (
                  <>
                    {messages.map((message, index) => (
                      <div
                        key={index}
                        className={`flex ${
                          message.sender === "user"
                            ? "justify-end"
                            : "justify-start"
                        }`}
                      >
                        <div className="flex items-end space-x-2 max-w-[90%] sm:max-w-[75%] relative">
                          {/* Message Bubble */}
                          <div
                            className={`px-3 py-2 rounded-lg shadow-sm ${
                              message.sender === "user"
                                ? "bg-[#d9fdd3] text-[#111b21] rounded-tr-none"
                                : "bg-white text-[#111b21] rounded-tl-none"
                            }`}
                          >
                            <div
                              className="leading-relaxed text-[15px] max-w-none [&_ul]:list-inside [&_ul]:list-disc [&_ul_li]:mb-1 [&_ol]:list-inside [&_ol]:list-decimal [&_ol_li]:mb-1 text-[#111b21] prose prose-sm prose-p:my-1"
                              style={{ fontFamily: "Inter, sans-serif" }}
                              dangerouslySetInnerHTML={{
                                __html: renderContent(message.text)
                              }}
                            />
                            <div className="text-[11px] text-[#667781] text-right mt-1 ml-4 min-w-[40px] float-right inline-flex items-center gap-1">
                              {new Date().toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit"
                              })}
                              {message.sender === "user" && (
                                <svg
                                  viewBox="0 0 16 15"
                                  width="16"
                                  height="15"
                                  className="text-[#53bdeb] fill-current inline"
                                >
                                  <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.366.366 0 0 0-.515.006l-.423.433a.364.364 0 0 0 .006.514l3.258 3.185c.143.14.361.125.484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"></path>
                                </svg>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Typing Indicator */}
                    {typing && (
                      <div className="flex justify-start">
                        <div className="flex items-end space-x-2 max-w-[90%] sm:max-w-[75%] relative">
                          <div className="bg-white px-3 py-2 rounded-lg rounded-tl-none shadow-sm flex items-center gap-2">
                            <span
                              className="text-[13px] text-[#667781] italic"
                              style={{ fontFamily: "Inter, sans-serif" }}
                            >
                              Typing
                            </span>
                            <div className="flex items-center gap-[3px]">
                              <span className="w-[5px] h-[5px] bg-[#8696a0] rounded-full animate-[typingDot_1.4s_ease-in-out_infinite]" />
                              <span className="w-[5px] h-[5px] bg-[#8696a0] rounded-full animate-[typingDot_1.4s_ease-in-out_0.2s_infinite]" />
                              <span className="w-[5px] h-[5px] bg-[#8696a0] rounded-full animate-[typingDot_1.4s_ease-in-out_0.4s_infinite]" />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>
          </div>

          {/* Enhanced Input Area - Unified design */}
          <div className="flex-shrink-0 px-3 sm:px-4 py-3 z-10 bg-[#f0f2f5]">
            <div className="relative flex items-center gap-2">
              <div className="flex-1 flex items-center bg-white rounded-lg px-2 shadow-sm border border-transparent focus-within:border-gray-300 transition-colors duration-200">
                {/* Emoji Button */}
                <div className="relative">
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="p-2 text-[#54656f] hover:text-[#111b21] transition-colors"
                  >
                    <Smile size={24} />
                  </button>
                  {showEmojiPicker && (
                    <div className="absolute bottom-full left-0 mb-3">
                      <div className="rounded-xl overflow-hidden shadow-xl border border-gray-200">
                        <EmojiPicker onEmojiClick={handleEmojiClick} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Input Field */}
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message"
                  className="flex-1 px-3 py-3 bg-transparent text-[#111b21] placeholder-[#8696a0] resize-none focus:outline-none text-[15px]"
                  rows={1}
                  style={{
                    minHeight: "44px",
                    maxHeight: "120px",
                    fontFamily: "Inter, sans-serif"
                  }}
                />
              </div>

              {/* Send Button */}
              <button
                onClick={() => {
                  if (input.trim()) {
                    onSendMessage(input);
                  }
                }}
                disabled={input.trim() === ""}
                className="p-3 bg-[#00a884] text-white hover:bg-[#008f6f] disabled:bg-[#f0f2f5] disabled:text-[#8696a0] rounded-full transition-colors flex items-center justify-center min-w-[48px] h-[48px]"
              >
                <Send
                  size={20}
                  className={`${input.trim() !== "" ? "translate-x-0.5" : ""}`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatInterface;
